/* Common Imports */

import { styled, useTheme } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import { setChatListTabThunk } from "../../../../redux-magic/store";

/* Component Imports */

import { Badge, Tab, Tabs, Typography } from "@mui/material";
import BroadcastList from "./BroadcastList";
import ChatList from "./ChatList";
import ChatListHeader from "./ChatListHeader";

/* Icon Imports */

import { ChatOutlined, ChevronRightOutlined, PersonAddAltOutlined, PersonAddOutlined } from "@mui/icons-material";
import ChatSearch from "./ChatSearch";

/* Styled Components */

const RootDiv = styled("div")(({ theme }) => ({
	background: theme.palette.background.paper,
	borderRadius: "0rem",
	height: "100%",
	width: "100%",
	padding: "0rem 0rem 0rem 0rem",
	[theme.breakpoints.up("md1190")]: {
		padding: "0rem 0rem 0rem 0rem",
	},
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
}));

const NewRequestNotification = styled("div")(({ theme }) => ({
	width: "100%",
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	gap: "0.5rem",
	background: "#dbf1e8",
	borderRadius: "1rem",
	padding: "1rem 1rem 1rem 1rem",
	boxShadow: "0px 4px 18px 0px rgba(0, 0, 0, 0.05)",
	cursor: "pointer",
	transition: "all ease-in-out 100ms",
	"&:hover": {
		background: "#cce8d9",
		boxShadow: "0px 6px 20px 0px rgba(0, 0, 0, 0.1)",
	},
}));

const CustomTab = styled(Tab)(({ theme }) => ({
	padding: "0rem 1rem 0rem 1rem",
	textTransform: "none",
	borderRadius: "2rem",
	minHeight: "2.5rem",
	maxHeight: "2.5rem",
	background: "#eeeeee",
	color: "#6d6d6d",
	overflow: "visible",
}));

const TabIconTitleRow = styled("div")(({ theme }) => ({
	width: "100%",
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	gap: "0.5rem",
}));

const TabTitle = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
}));

const ChatListScreen = ({
	session,
	dispatch,
	router,
	profilePicture,
	profile_context,
	chat_list,
	broadcast_list,
	list_tab,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	profilePicture: string;
	profile_context: any;
	chat_list: StateInterface["chat_states"]["chat_list"];
	broadcast_list: StateInterface["chat_states"]["broadcast_list"];
	list_tab: string;
}) => {
	const theme = useTheme();

	/* Translations */

	const localLang = {
		chats: router.locale === "ar-AE" ? "الدردشات" : router.locale === "kn-IN" ? "ಚಾಟ್‌ಗಳು" : "Chats",
		requests: router.locale === "ar-AE" ? "طلبات" : router.locale === "kn-IN" ? "ವಿನಂತಿಗಳು" : "Requests",
		new_connection_request:
			router.locale === "ar-AE"
				? "طلب اتصال جديد"
				: router.locale === "kn-IN"
					? "ಹೊಸ ಸಂಪರ್ಕ ವಿನಂತಿ"
					: "New connection requests",
		view_all: router.locale === "ar-AE" ? "عرض الكل" : router.locale === "kn-IN" ? "ಎಲ್ಲಾ ವೀಕ್ಷಿಸಿ" : "view all",
	};

	const [clearAutoComplete, setClearAutoComplete] = React.useState<boolean>(false);
	const [searchInputValue, setSearchInputValue] = React.useState<string>("");

	// if (chat_list.chats_loading) {
	// 	//TODO: Skeleton
	// 	return <div>loading</div>;
	// }

	return (
		<React.Fragment>
			<RootDiv>
				{/* Chat List Header */}

				<ChatListHeader
					session={session}
					dispatch={dispatch}
					router={router}
					profilePicture={profilePicture}
					profile_context={profile_context}
					loading={chat_list.chats_loading}
				/>

				{/* Tabs */}

				<Tabs
					TabIndicatorProps={{
						style: { opacity: "0" },
					}}
					value={list_tab}
					orientation="horizontal"
					variant="fullWidth"
					sx={{
						padding: "0.25rem 1rem 0.5rem 1rem",
						"& .Mui-selected": {
							background: "#feeada",
							color: "#6e3037 !important",
							border: "none",
						},
						"& .MuiTabs-flexContainer": {
							gap: "1rem",
							padding: "0.75rem 0.5rem 0.5rem 0.5rem",
							overflow: "visible",
						},
					}}
				>
					<CustomTab
						disableRipple
						disableFocusRipple
						disableTouchRipple
						value="chats"
						onClick={() => {
							dispatch(setChatListTabThunk("chats"));
						}}
						label={
							<Badge
								badgeContent={chat_list.total_unread_count > 9 ? "9+" : chat_list.total_unread_count}
								sx={{
									"& .MuiBadge-badge": {
										background: theme.palette.primary.main,
										color: theme.palette.primary.contrastText,
										top: -8,
										right: -18,
										border: `2px solid ${theme.palette.background.paper}`,
										height: "1.5rem",
										width: chat_list.total_unread_count > 9 ? "2rem" : "1.5rem",
										fontSize: "0.875rem",
										fontWeight: 600,
										borderRadius: chat_list.total_unread_count > 9 ? "1rem" : "50%",
									},
								}}
							>
								<TabIconTitleRow>
									<ChatOutlined />

									<TabTitle
										sx={{
											textAlign: router.locale === "ar-AE" ? "right" : "left",
											fontWeight: list_tab === "chats" ? 600 : 400,
										}}
									>
										{localLang.chats}
									</TabTitle>
								</TabIconTitleRow>
							</Badge>
						}
					/>

					<CustomTab
						disableRipple
						disableFocusRipple
						disableTouchRipple
						value="broadcasts"
						onClick={() => {
							dispatch(setChatListTabThunk("broadcasts"));
						}}
						label={
							<Badge
								badgeContent={broadcast_list.broadcasts_count > 9 ? "9+" : broadcast_list.broadcasts_count}
								sx={{
									"& .MuiBadge-badge": {
										background: theme.palette.primary.main,
										color: theme.palette.primary.contrastText,
										top: -8,
										right: -10,
										border: `2px solid ${theme.palette.background.paper}`,
										height: "1.5rem",
										width: broadcast_list.broadcasts_count > 9 ? "2rem" : "1.5rem",
										fontSize: "0.875rem",
										fontWeight: 600,
										borderRadius: broadcast_list.broadcasts_count > 9 ? "1rem" : "50%",
									},
								}}
							>
								<TabIconTitleRow>
									<PersonAddOutlined />

									<TabTitle
										sx={{
											textAlign: router.locale === "ar-AE" ? "right" : "left",
											fontWeight: list_tab === "broadcasts" ? 600 : 400,
										}}
									>
										{localLang.requests}
									</TabTitle>
								</TabIconTitleRow>
							</Badge>
						}
					/>
				</Tabs>

				{list_tab === "broadcasts" ? (
					<BroadcastList
						session={session}
						dispatch={dispatch}
						router={router}
						profile_context={profile_context}
						broadcast_list={broadcast_list}
						list_tab={list_tab}
					/>
				) : (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							width: "100%",
						}}
					>
						{/* New Request Notification */}

						{broadcast_list.broadcasts_count > 0 ? (
							<RowContainer
								sx={{
									padding: "1rem 1rem 1rem 1rem",
									width: "100%",
								}}
							>
								<NewRequestNotification
									onClick={() => {
										dispatch(setChatListTabThunk("broadcasts"));
									}}
								>
									<RowContainer
										sx={{
											gap: "0.5rem",
										}}
									>
										<PersonAddAltOutlined
											sx={{
												fontSize: "1.5rem",
											}}
										/>
										<Typography
											variant="body2"
											sx={{
												fontWeight: 500,
											}}
										>
											{localLang.new_connection_request}
										</Typography>
									</RowContainer>

									<RowContainer
										sx={{
											gap: "0.25rem",
										}}
									>
										<Typography
											variant="subtitle2"
											sx={{
												fontSize: "0.75rem",
											}}
										>
											{localLang.view_all}
										</Typography>
										<ChevronRightOutlined />
									</RowContainer>
								</NewRequestNotification>
							</RowContainer>
						) : null}

						{/* Search Chats */}

						<ChatSearch
							session={session}
							dispatch={dispatch}
							router={router}
							searchInputValue={searchInputValue}
							setSearchInputValue={setSearchInputValue}
							clearAutoComplete={clearAutoComplete}
							setClearAutoComplete={setClearAutoComplete}
						/>

						<ChatList
							session={session}
							dispatch={dispatch}
							router={router}
							profile_context={profile_context}
							chat_list={chat_list}
							broadcast_list={broadcast_list}
							search_term={searchInputValue}
							clearAutoComplete={clearAutoComplete}
							list_tab={list_tab}
						/>
					</div>
				)}
			</RootDiv>
		</React.Fragment>
	);
};

export default ChatListScreen;
